import React, { Component } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { ValidationError, ExpandingTextarea, HelpText, IconCheckmark } from '../../components';

import css from './FieldTextInput.module.css';

const CONTENT_MAX_LENGTH = 5000;

const FieldTextInputComponent = props => {
  const {
    rootClassName,
    className,
    inputRootClass,
    labelClassName,
    customErrorText,
    helpText,
    helpTextClassName,
    id,
    label,
    input,
    meta,
    onUnmount,
    isUncontrolled,
    inputRef,
    hideErrorMessage,
    floatingLabel,
    placeholder,
    showValidIndicator,
    validMessage,
    ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, error } = meta;
  const isTextarea = input.type === 'textarea';
  const hasFloatingLabel = !!(floatingLabel && label);
  const inputValue = input.value;
  const hasValue =
    typeof inputValue === 'string'
      ? inputValue.trim().length > 0
      : inputValue !== null && inputValue !== undefined;

  const errorText = customErrorText || error;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = !!customErrorText || !!(touched && invalid && error);
  const hasValidIndicator =
    !!showValidIndicator && !isTextarea && touched && valid && hasValue && !hasError;
  const validMessageId = hasValidIndicator && validMessage && id ? `${id}-valid` : null;
  const describedBy = [rest['aria-describedby'], validMessageId].filter(Boolean).join(' ');

  const fieldMeta = { touched: hasError, error: errorText };

  // Textarea doesn't need type.
  const { type, ...inputWithoutType } = input;
  // Uncontrolled input uses defaultValue instead of value.
  const { value: defaultValue, ...inputWithoutValue } = input;
  // Use inputRef if it is passed as prop.
  const refMaybe = inputRef ? { ref: inputRef } : {};

  const inputClasses = classNames(
    inputRootClass ||
      classNames(css.input, {
        [css.inputSuccess]: valid,
        [css.inputError]: hasError,
        [css.textarea]: isTextarea,
      }),
    {
      [css.floatingInput]: hasFloatingLabel,
      [css.floatingTextarea]: hasFloatingLabel && isTextarea,
      [css.inputWithValidIndicator]: !!showValidIndicator && !isTextarea,
    }
  );
  const maxLength = CONTENT_MAX_LENGTH;
  // Keep example copy available once the field has focus. A whitespace placeholder is still
  // needed when no example was provided so :placeholder-shown can detect an empty field.
  const placeholderMaybe = {
    placeholder: hasFloatingLabel ? placeholder || ' ' : placeholder,
  };
  const inputProps = isTextarea
    ? {
        className: inputClasses,
        id,
        rows: 1,
        maxLength,
        ...refMaybe,
        ...inputWithoutType,
        ...rest,
        ...placeholderMaybe,
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
      }
    : isUncontrolled
    ? {
        className: inputClasses,
        id,
        type,
        defaultValue,
        ...refMaybe,
        ...inputWithoutValue,
        ...rest,
        ...placeholderMaybe,
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
      }
    : {
        className: inputClasses,
        id,
        type,
        ...refMaybe,
        ...input,
        ...rest,
        ...placeholderMaybe,
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
      };

  const labelClassMaybe = labelClassName ? { className: labelClassName } : {};
  const classes = classNames(rootClassName || css.root, className);
  const inputElement = isTextarea ? (
    <ExpandingTextarea {...inputProps} />
  ) : (
    <input {...inputProps} />
  );
  const validIndicator = hasValidIndicator ? (
    <>
      <span className={css.validIndicator} aria-hidden="true">
        <IconCheckmark rootClassName={css.validIcon} size="small" />
      </span>
      {validMessageId ? (
        <span className={css.visuallyHidden} id={validMessageId}>
          {validMessage}
        </span>
      ) : null}
    </>
  ) : null;
  return (
    <div className={classes}>
      {hasFloatingLabel ? (
        <div className={css.floatingInputWrapper}>
          {inputElement}
          <label className={classNames(css.floatingLabel, labelClassName)} htmlFor={id}>
            {label}
          </label>
          {validIndicator}
        </div>
      ) : label ? (
        <label htmlFor={id} {...labelClassMaybe}>
          {label}
        </label>
      ) : null}
      {hasFloatingLabel ? null : showValidIndicator && !isTextarea ? (
        <div className={css.inputWrapper}>
          {inputElement}
          {validIndicator}
        </div>
      ) : (
        inputElement
      )}
      <HelpText className={helpTextClassName} helpText={helpText} />
      {hideErrorMessage ? null : <ValidationError fieldMeta={fieldMeta} />}
    </div>
  );
};

/**
 * Create Final Form field for <input> or <textarea>.
 * It's often used for type="text" and sometimes with other types like 'number' too.
 *
 * Note: Uncontrolled input uses defaultValue prop, but doesn't pass value from form to the field.
 * https://reactjs.org/docs/uncontrolled-components.html#default-values
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.inputRootClass overwrite components own css.input
 * @param {string} props.name Name of the input in Final Form
 * @param {string} props.id
 * @param {string?} props.label Label is optional, but if it is given, an id is also required.
 * @param {boolean?} props.floatingLabel render the label inside the field until it has focus or a value
 * @param {boolean?} props.showValidIndicator show a checkmark for a touched, non-empty, valid input
 * @param {React.ReactNode?} props.validMessage optional accessible description for the valid state
 * @param {string?} props.customErrorText Error message that can be manually passed to input field, overrides default validation message
 * @param {string?} props.helpTextClassName add more style rules to the help text
 * @param {boolean} props.isUncontrolled is value tracked by parent component
 * @param {Object} props.inputRef a ref object passed for input element.
 * @param {Function} props.onUnmount Uncontrolled input uses defaultValue prop, but doesn't pass value from form to the field.
 * @returns {JSX.Element} Final Form Field containing nested "select" input
 */
class FieldTextInput extends Component {
  componentWillUnmount() {
    // Unmounting happens too late if it is done inside Field component
    // (Then Form has already registered its (new) fields and
    // changing the value without corresponding field is prohibited in Final Form
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  }

  render() {
    return <Field component={FieldTextInputComponent} {...this.props} />;
  }
}

export default FieldTextInput;
